import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import toast, {
  Toaster as HotToaster,
  useToasterStore,
  type ToasterProps as HotToasterProps,
} from "react-hot-toast";

import { DEMO_BLOCKED_EVENT, isDemoToastSuppressed } from "../../utils/demoMode";
import "./toast-animations.scss";

/**
 * SOREMO 標準のトースト Toaster ラッパー。
 *
 * `react-hot-toast` の `<Toaster />` をプロジェクト共通のデザインで包む。
 *  - 白背景 + 本文ダーク + box-shadow + 角丸
 *  - エラーのみ accent カラー（left border）で差別化
 *  - **入退場ともに短尺の blur アニメ** を適用。`<Toaster />` の render prop で
 *    完全自前の div を返し、`react-hot-toast` 内部の slide-in/out 計算をバイパス
 *    する。`t.visible` で `text-focus-in-fast` / `text-blur-out-fast` を切り替え、
 *    入退場ともに blur で統一する。
 *  - 表示位置は Redux 上のスクロール状態（`state.layout`）から動的に算出する。
 *      - GlobalHeader / BottomAppBar が表示中（`state.layout !== "down"`）:
 *          通常通知 = `top: 64, right: 0`
 *          マイルストーン = `bottom: 80, left: 0`
 *      - 隠れているとき（下スクロール中）:
 *          通常通知 = `top: 0, right: 0`
 *          マイルストーン = `bottom: 0, left: 0`
 *  - スクロール連動の切り替えは GlobalWrapper の `padding-top` transition
 *    （0.15s linear）に揃え、GlobalHeader / BottomAppBar の出現/消失と
 *    滑らかに同期する。
 *
 * `react-hot-toast` の `<Toaster />` は内部に position 別の子コンテナを生成し、
 * トースト個別の `position` プロパティで振り分ける。`containerStyle` は最外殻
 * div のスタイルで、`top` / `bottom` / `left` / `right` をすべて指定しておけば、
 * 各 position 子コンテナはそれぞれ自分に意味のある値を使う。
 */
export interface SoremoToasterProps {
  /** 既定の表示時間 (ms)。既定 2000。 */
  duration?: number;
}

const SOREMO_TOAST_BASE_STYLE: React.CSSProperties = {
  background: "#fff",
  color: "#000",
  fontSize: "14px",
  padding: "12px 16px",
  borderRadius: "6px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  margin: "4px",
  maxWidth: "min(360px, calc(100vw - 32px))",
};

/** GlobalHeader 高さ。`top` オフセットに使う。 */
const GLOBAL_HEADER_HEIGHT = 64;
/** BottomAppBar 高さ。`bottom` オフセットに使う。 */
const BOTTOM_APP_BAR_HEIGHT = 80;
/** GlobalHeader / BottomAppBar の出現/消失と同じ transition タイミング。 */
const POSITION_TRANSITION = "top 0.15s linear, bottom 0.15s linear";

/**
 * Redux `state.layout` の型。
 * scrollSlice (`src/app/actions/layout/scroll.js`) で `"init" | "up" | "down"`
 * の文字列を保持している。`"down"` のとき = ユーザーが下スクロール中で、
 * `GlobalHeader` / `BottomAppBar` が画面外にスライドアウトしている。
 */
type LayoutState = "init" | "up" | "down";

const SoremoToaster: React.FC<SoremoToasterProps> = ({
  duration = 2000,
}) => {
  const scrollState = useSelector(
    (state: { layout: LayoutState }) => state.layout,
  );
  const isBarVisible = scrollState !== "down";

  // デモモードの書き込み遮断時は DemoBlockedModal が説明を担うため、
  // トーストは出さない。遮断イベントで表示中のトースト（送信前の
  // toast.loading 等）を即時除去し、直後に発行される「保存に失敗しました」
  // 等は render prop 側の isDemoToastSuppressed() で描画を抑止する。
  useEffect(() => {
    const handleDemoBlocked = () => toast.remove();
    window.addEventListener(DEMO_BLOCKED_EVENT, handleDemoBlocked);
    return () =>
      window.removeEventListener(DEMO_BLOCKED_EVENT, handleDemoBlocked);
  }, []);

  // 抑止ウィンドウ中に新規発行されたトーストはストアからも除去する
  // （duration が長いトーストがウィンドウ明けに再表示されるのを防ぐ）。
  // 描画自体は render prop 側の早期 return が抑えるため、一瞬の表示もない。
  const { toasts } = useToasterStore();
  useEffect(() => {
    if (!isDemoToastSuppressed()) return;
    toasts.forEach((item) => toast.remove(item.id));
  }, [toasts]);

  const containerStyle: React.CSSProperties = {
    top: isBarVisible ? GLOBAL_HEADER_HEIGHT : 0,
    bottom: isBarVisible ? BOTTOM_APP_BAR_HEIGHT : 0,
    left: 0,
    right: 0,
    transition: POSITION_TRANSITION,
  };

  return (
    <HotToaster
      position={"top-right" as HotToasterProps["position"]}
      reverseOrder={false}
      containerStyle={containerStyle}
      toastOptions={{
        duration,
        style: SOREMO_TOAST_BASE_STYLE,
        success: {
          style: SOREMO_TOAST_BASE_STYLE,
          iconTheme: {
            primary: "var(--soremo-blue)",
            secondary: "#fff",
          },
        },
        error: {
          style: {
            ...SOREMO_TOAST_BASE_STYLE,
            borderLeft: "4px solid var(--soremo-green, #2ec27e)",
          },
        },
      }}
    >
      {(t) => {
        // デモ遮断直後のトーストは描画しない（モーダルとの二重通知の防止）
        if (isDemoToastSuppressed()) {
          return <></>;
        }
        // ToastBar を使わず完全自前の div を返すことで、`react-hot-toast` 内部の
        // slide-in/out アニメーション計算をバイパスする。これで `animation` 指定が
        // 確実に効き、退場時も blur フェードアウトする。
        // - 個別 toast の `t.style`（error の左ボーダー等）は最後に重ねて反映。
        // - `t.message` が関数渡しのケースもあるので、関数なら呼び出して描画。
        const message =
          typeof t.message === "function" ? t.message(t) : t.message;
        return (
          <div
            style={{
              ...SOREMO_TOAST_BASE_STYLE,
              ...t.style,
              animation: t.visible
                ? "text-focus-in-fast 0.4s cubic-bezier(.55,.085,.68,.53) both"
                : "text-blur-out-fast 0.4s cubic-bezier(.55,.085,.68,.53) both",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              pointerEvents: t.visible ? "auto" : "none",
            }}
          >
            {t.icon}
            <div>{message}</div>
          </div>
        );
      }}
    </HotToaster>
  );
};

export default SoremoToaster;
